import React from "react";

import { useController, useFormContext } from "react-hook-form";

import { Field, FieldLabel } from "@/src/components/ui/field";
import { ToggleGroup, ToggleGroupItem } from "@/src/components/ui/toggle-group";
import { cn } from "@/src/lib/utils";

import type { ComponentProps, JSX, ReactNode } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

/** Grey track that holds segmented cells. Reused by `PillField`. */
export const SEGMENT_TRACK_CLASS =
  "bg-muted flex w-full items-center gap-1 rounded-md p-1";

/** A single segmented cell (toggle), highlighted when selected. */
export const SEGMENT_ITEM_CLASS =
  "hover:text-foreground text-muted-foreground data-[state=on]:bg-card data-[state=on]:text-primary h-8 flex-1 rounded-md text-sm font-medium data-[state=on]:shadow-sm";

const PRIMARY_TRACK_CLASS = cn(SEGMENT_TRACK_CLASS, "inline-flex w-fit");

const PRIMARY_ITEM_CLASS = cn(
  SEGMENT_ITEM_CLASS,
  "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground flex-none px-4",
);

type SegmentedVariant = "default" | "primary";

interface SegmentedVariantClass {
  field: string | undefined;
  track: string;
  item: string;
}

const VARIANT_CLASS_MAP: Record<SegmentedVariant, SegmentedVariantClass> = {
  default: {
    field: undefined,
    track: SEGMENT_TRACK_CLASS,
    item: SEGMENT_ITEM_CLASS,
  },
  primary: {
    field: "w-fit",
    track: PRIMARY_TRACK_CLASS,
    item: PRIMARY_ITEM_CLASS,
  },
};

interface SegmentedOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

interface SegmentedFieldBaseProps {
  label?: string;
  ariaLabel?: string;
  variant?: SegmentedVariant;
  value: string;
  options: SegmentedOption[];
  disabled?: boolean;
  onValueChange: (value: string) => void;
}

export function SegmentedFieldBase({
  label,
  ariaLabel,
  variant = "default",
  value,
  options,
  disabled,
  onValueChange,
}: SegmentedFieldBaseProps): JSX.Element {
  const variantClass = VARIANT_CLASS_MAP[variant];

  return (
    <Field className={variantClass.field}>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <ToggleGroup
        aria-label={ariaLabel}
        className={variantClass.track}
        disabled={disabled}
        spacing={1}
        type="single"
        value={value}
        onValueChange={(next: string) => {
          if (next) onValueChange(next);
        }}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            className={variantClass.item}
            disabled={option.disabled}
            value={option.value}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Field>
  );
}

interface SegmentedFieldProps<T extends FieldValues> {
  name: FieldPathByValue<T, string | number | undefined>;
  options: ComponentProps<typeof SegmentedFieldBase>["options"];
  label?: string;
  disabled?: boolean;
  /**
   * Converts the selected option (always a string) back to the stored field
   * value. Omit for string fields; pass `Number` for numeric ones (frequency).
   */
  parse?: (value: string) => unknown;
}

/**
 * `SegmentedField` bound to a react-hook-form field via `useController`.
 * `FieldPathByValue<T, string | number | undefined>` restricts binding to the
 * fields a segmented control can back — string ones (platform, resolution) and
 * numeric ones (frequency) — excluding booleans/objects. The option value is
 * stringified for display and `parse`d back to a number on change.
 */
export function SegmentedField<T extends FieldValues>({
  name,
  options,
  label,
  disabled,
  parse,
}: SegmentedFieldProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field } = useController({ control, name });

  return (
    <SegmentedFieldBase
      disabled={disabled}
      label={label}
      options={options}
      value={field.value == null ? "" : String(field.value)}
      onValueChange={(value: string) =>
        field.onChange(parse ? parse(value) : value)
      }
    />
  );
}
